import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { landingContentBySlug } from "../../config/landing";
import { designedLandingPages } from "../../landingPages";
import { useCatalogDirectory } from "../../hooks/useCatalogDirectory";
import ProductLanding from "../ProductLanding/ProductLanding";

export default function ProductLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const { categories, loading } = useCatalogDirectory();

  // The app shell scrolls #main-scroll-area, not the window, and React
  // Router keeps its position across navigation — without this, arriving
  // from the catalog grid lands mid-page.
  useEffect(() => {
    document.getElementById("main-scroll-area")?.scrollTo(0, 0);
  }, [slug]);

  const match = useMemo(() => {
    for (const category of categories) {
      const product = category.products.find((entry) => entry.slug === slug);
      if (product) {
        return { product, categoryName: category.name };
      }
    }
    return null;
  }, [categories, slug]);

  if (loading) {
    return <LoadingIndicator />;
  }

  const key = match?.product.landingPageKey ?? null;
  const DesignedPage = key ? designedLandingPages[key] : undefined;
  if (match && DesignedPage) {
    return <DesignedPage />;
  }

  const content = key ? landingContentBySlug[key] : undefined;

  if (!match || !content) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Page not found
        </h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          This product doesn&apos;t have a page yet.
        </p>
        <Link
          className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          to="/catalog"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Product Catalog
        </Link>
      </div>
    );
  }

  return (
    <ProductLanding
      categoryName={match.categoryName}
      content={content}
      product={match.product}
    />
  );
}
